import React, { useEffect, useState } from "react";
import { get, omitBy } from "lodash/fp";
import {
  Container,
  Pagination,
  Table,
  Footer,
  Content,
  Button,
  Form,
  Input,
  Col,
  InputGroup,
  SelectPicker,
  Checkbox,
  Header,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import SaveIcon from "@rsuite/icons/AddOutline";
import CloseIcon from "@rsuite/icons/Close";
import { BrandFields, BrandListParams } from "./BrandDataTypes";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import { updateIn } from "json-immut";
import MasterEditCell from "../../components/Elements/MasterEditCell";
import ActionCell from "./ActionCell";
import { AlertModal } from "../../components/Modals/AlertModal/AlertModal";
import { useTranslation } from "react-i18next";
import { HelperServices } from "../../../core/services/HelperServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import {
  appConstObj,
  messageTypeArr,
  roleTypeArr,
} from "../../../core/config/AppConstants";
import "./Brands.css";
import { isEmptyVal } from "../../../utils/helpers";
import CheckCell from "../../components/Elements/CheckCell";
import { useSelector } from "react-redux";

function Brands({
  withHeader = true,
  multiSelect = true,
  showCreateForm = true,
  forComp = null,
  onSelectBrand,
  ...props
}: {
  withHeader?: boolean;
  multiSelect?: boolean;
  showCreateForm?: boolean;
  forComp?: number;
  onSelectBrand?: Function;
}) {
  const { Column } = Table;
  const company = useSelector((state: any) => state.company);
  const [loading, setLoading] = useState(false);
  const [brandId, setBrandId] = useState(0);
  const [data, setData] = useState<BrandFields[]>([]);
  const [editData, setEditData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [page, setPage] = useState(1);
  const [sort_by, setSortBy] = useState();
  const [sort_order, setSortOrder] = useState();
  const [errors, setErrors] = useState<any>({});
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [smartSearchInput, setSmartSearch] = useState<string>("");
  const [openNoBrandModal, setOpenNoBrandModal] = useState(false);
  const { t } = useTranslation();
  const initSearchFormData = {
    name: "",
    status: "",
  };

  const initFormSaveData = {
    brand_name: "",
    status: 1,
  };

  const [formData, setFormData] = useState<BrandListParams>(initSearchFormData);

  const [formSaveData, setFormSaveData] =
    useState<BrandFields>(initFormSaveData);

  const [showCreate, setShowCreate] = useState<boolean>(showCreateForm);

  const [checkedKeys, setCheckedKeys] = useState<any[]>([]);
  let checked = false;
  let indeterminate = false;

  if (checkedKeys?.length === data?.length) {
    checked = true;
  } else if (checkedKeys?.length === 0) {
    checked = false;
  } else if (checkedKeys?.length > 0 && checkedKeys?.length < data?.length) {
    indeterminate = true;
  }

  const handleCheckAll = (value: any, checked: any) => {
    const keys = checked ? data.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };

  const handleCheck = (value: number, checked: any) => {
    let keys: any;
    if (!checked) {
      keys = checkedKeys.filter((item) => item !== value);
    } else if (checked && multiSelect) {
      keys = [...checkedKeys, value];
    } else if (checked && !multiSelect) {
      keys = [value];
    }
    setCheckedKeys(keys);
  };

  function setSelectedBrand() {
    if (checkedKeys.length === 0) {
      setOpenNoBrandModal(true);
      return true;
    } else {
      const brandData = data
        .filter((item) => item.id == checkedKeys[0])
        .shift();
      onSelectBrand && onSelectBrand(brandData);
    }
  }

  async function getBrands(params: BrandListParams) {
    try {
      setLoading(true);
      const res = await HelperServices.getBrands(params);
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      if (!showCreateForm) {
        if (get("data.total", res) <= 0) {
          setShowCreate(UtilityServices.hasPermission("create-song"));
        } else {
          setShowCreate(false);
        }
      }

      setCheckedKeys([]);
      setLoading(false);
    } catch (error) {
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
      setLoading(false);
    }
  }

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ name: smartSearchInput },
      ...{ company: getCompanyId() },
      ...{ sort_by, sort_order },
      ...{ rows },
      ...data,
    };
    let params: BrandListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }
    return params;
  }

  const handleEditData = async (id: Number) => {
    let nextData: any = {};
    let error: string = "";
    let activeItem: any = {};
    nextData = Object.assign([], data);
    activeItem = nextData?.find((item: any) => item.id === id);
    activeItem.editStatus = activeItem?.editStatus ? null : "EDIT";

    if (activeItem.editStatus == null) {
      if (isEmptyVal(activeItem.brand_name))
        error = t("REQUIRED", {
          field: t("BRAND").toLowerCase() + " " + t("NAME").toLowerCase(),
        });

      if (!isEmptyVal(error)) {
        UtilityServices.showToaster({
          message: error,
          type: messageTypeArr.error,
        });
        activeItem.editStatus = activeItem?.editStatus ? null : "EDIT";
        return;
      }

      let params = {
        brand_name: activeItem.brand_name,
        status: activeItem.status,
      };

      try {
        setLoading(true);
        const response: any = await HelperServices.updateBrand(
          activeItem.id,
          params
        );
        UtilityServices.showToaster({
          message: response?.data?.message,
        });
        setPage(1);
        setFormSaveData(formSaveData);
        getBrands(
          getSearchParams({
            page: page,
            sort_by: sort_by,
            sort_order: sort_order,
            rows: rows,
          })
        );

        setLoading(false);
      } catch (error: any) {
        UtilityServices.showToaster({
          message: error?.message,
          type: messageTypeArr.error,
        });
        setLoading(false);
        activeItem.editStatus = activeItem?.editStatus ? null : "EDIT";
      }
    }

    setEditData(nextData);
  };

  const handleDataChanges = (id: Number, key: any, value: any) => {
    let nextData: any = {};
    nextData = Object.assign([], data);
    nextData.find((item: any) => item.id === id)[key] =
      key == "status" ? (value ? 1 : 0) : value;
    setEditData(nextData);
  };

  const clearSaveForm = () => {
    setErrors({});
    setFormSaveData(initFormSaveData);
  };

  async function addBrand() {
    try {
      let i;
      let data = { ...formSaveData };
      let dumpError = { ...errors };
      if (
        UtilityServices.getLoggedInUser("user_type") == roleTypeArr.backend &&
        !company.id
      ) {
        UtilityServices.showToaster({
          message: t("REQUIRED_SELECT", {
            field: t("COMPANY").toLowerCase(),
          }),
          type: messageTypeArr.error,
        });
        return false;
      }
      for (i in data) {
        if (data[i] === "") {
          dumpError[i] = t("FIELD_REQUIRED");
          setErrors(dumpError);
        } else {
          delete dumpError[i];
          setErrors(dumpError);
        }
      }

      if (Object.keys(dumpError).length == 0) {
        setLoading(true);
        data.brand_name = data.brand_name;
        data.company_id = getCompanyId();
        const response: any = await HelperServices.createBrand(data);
        UtilityServices.showToaster({
          message: response?.data?.message,
        });

        setPage(1);
        setFormSaveData(initFormSaveData);
        getBrands(
          getSearchParams({
            page: page,
            sort_by: sort_by,
            sort_order: sort_order,
            rows: rows,
          })
        );
        setLoading(false);
      }
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
      setLoading(false);
    }
  }

  const handleFormChange = (data: any) => {
    if (data.path == "status") {
      if (data.value) {
        data.value = 1;
      } else {
        data.value = 0;
      }
    }
    let form = updateIn(formSaveData, [data.path], data.value);
    setFormSaveData(form);
  };

  const handleSearchFormChange = (data: any) => {
    let form = updateIn(formData, [data.path], data.value);
    setFormData(form);
  };

  const handleDataDelete = async (brandId: number) => {
    try {
      setLoading(true);
      const response: any = await HelperServices.deleteBrand(brandId);
      if (response.status) {
        UtilityServices.showToaster({
          message: response?.data?.message,
        });
        setPage(1);
        getBrands(
          getSearchParams({
            page: page,
            sort_by: sort_by,
            sort_order: sort_order,
            rows: rows,
          })
        );
      } else {
        UtilityServices.showToaster({
          message: response?.data?.message,
        });
      }
      setLoading(false);
    } catch (error: any) {
      console.log(error);
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
      setLoading(false);
    }
  };

  function onPageChange(page: number) {
    setPage(page);
    getBrands(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getBrands(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  const handleChangeRows = (rows: any) => {
    setPage(1);
    setRows(rows);
    getBrands(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };

  const filterBrands = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getBrands(
        getSearchParams({
          page: page,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getBrands(
      getSearchParams({
        name: "",
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };

  async function init() {
    try {
      setShowCreate(
        UtilityServices.hasPermission("create-song") && showCreateForm
      );
    } catch (error: any) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
    if (getCompanyId() !== null) {
      getBrands(
        getSearchParams({
          page: page,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
    }
  }, []);

  useEffect(() => {
    if (company.id) {
      getBrands(
        getSearchParams({
          company: company.id,
          page: page,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
    }
    setPage(1);
  }, [company]);

  function getCompanyId() {
    let compId = null;
    if (UtilityServices.getLoggedInUser("user_type") === roleTypeArr.client) {
      compId = UtilityServices.getLoggedInUser("company_id");
    } else if (forComp) {
      compId = forComp;
    } else if (company?.id) {
      compId = company.id;
    }
    return compId;
  }

  return (
    <div
      className={`brands brand ${withHeader ? "brands-page" : "brands-picker"}`}
    >
      <Container className="container">
        {withHeader && (
          <Header className="header brand-page-header">
            <h4>{t("BRANDS")}</h4>
          </Header>
        )}
        <Content className="brands-content">
          {showCreate && (
            <Form formValue={formSaveData} className="entity-picker-createbar">
              <Col
                md={8}
                className="entity-picker-create-field entity-picker-create-field-wide"
              >
                <Form.Group controlId="brand_name">
                  <Form.Control
                    name="brand_name"
                    size="sm"
                    placeholder={t("BRAND") + " " + t("NAME")}
                    onChange={(v: any) =>
                      handleFormChange({ path: "brand_name", value: v })
                    }
                    value={formSaveData?.brand_name}
                  />
                </Form.Group>
                <span className="text-danger">{errors["brand_name"]}</span>
              </Col>
              <Col md={2} className="entity-picker-create-actions">
                <div className="entity-picker-create-buttons">
                  <Button title={t("ADD")} onClick={addBrand}>
                    <SaveIcon />
                  </Button>
                  <Button title={t("CLEAR")} onClick={clearSaveForm}>
                    <CloseIcon />
                  </Button>
                </div>
              </Col>
            </Form>
          )}
          <div className="row entity-picker-toolbar-row brand-controls-row">
            <div className="col-md-4 entity-picker-select-col">
              {onSelectBrand && (
                <Button
                  className="button entity-picker-select-btn"
                  appearance="primary"
                  onClick={() => setSelectedBrand()}
                >
                  {t("SELECT") + " " + t("BRAND")}
                </Button>
              )}
            </div>
            <div className="col-md-8 mb-2 text-right brand-search-col">
              <InputGroup className="entity-picker-searchbar brand-smart-search">
                <Input
                  className="form-control"
                  placeholder={t("SMART_SEARCH_BY", {
                    entity:
                      t("BRAND").toLowerCase() +
                      " " +
                      t("NAME").toLowerCase(),
                  })}
                  value={smartSearchInput}
                  onChange={(val: any) => setSmartSearch(val)}
                  onKeyDown={(e: any) => filterBrands(e)}
                />
                <InputGroup.Button
                  title={t("SEARCH")}
                  onClick={(e: any) => filterBrands(e)}
                >
                  <SearchIcon />
                </InputGroup.Button>
                <InputGroup.Button
                  title={t("CLEAR")}
                  onClick={() => clearSmartSearch()}
                >
                  <CloseIcon />
                </InputGroup.Button>
              </InputGroup>
            </div>
          </div>
          <div className={total > 0 ? "brand-table" : "brand-empty-wrap"}>
            <Table
              className="entity-picker-table"
              loading={loading}
              height={400}
              fillHeight={true}
              data={data}
              onSortColumn={onSortChange}
              sortColumn={sort_by}
              sortType={sort_order}
              bordered
              cellBordered
              hover={true}
              renderEmpty={() => (
                <div className="entity-picker-empty-state brand-empty-state">
                  <div className="entity-picker-empty-icon">
                    {getCompanyId() !== null ? "0" : "?"}
                  </div>
                  <h4 className="entity-picker-empty-title">
                    {getCompanyId() !== null
                      ? t("NO_ENTITY_FOUND", { entity: t("BRAND") })
                      : t("PLEASE_SELECT_FIELD", { field: t("COMPANY") })}
                  </h4>
                  <p className="entity-picker-empty-text">
                    {getCompanyId() !== null
                      ? t("ADJUST_FILTER") +
                        " " +
                        t("OR") +
                        " " +
                        t("CREATE_NEW_ENTITY", { entity: t("BRAND") })
                      : t("PLEASE_SELECT_FIELD", { field: t("COMPANY") })}
                  </p>
                </div>
              )}
            >
          <Column width={40} align="center" fixed>
            <CustomHeaderCell>
              <div>
                {multiSelect && (
                  <Checkbox
                    inline
                    checked={checked}
                    indeterminate={indeterminate}
                    onChange={handleCheckAll}
                  />
                )}
              </div>
            </CustomHeaderCell>
            <CheckCell
              dataKey="id"
              checkedKeys={checkedKeys}
              onChange={handleCheck}
            />
          </Column>
          <Column flexGrow={1} align="left" fixed sortable>
            <CustomHeaderCell>{t("BRAND") + " " + t("NAME")}</CustomHeaderCell>
            <MasterEditCell dataKey="brand_name" onChange={handleDataChanges} />
          </Column>
          <Column flexGrow={1} align="center" sortable>
            <CustomHeaderCell>{t("STATUS")}</CustomHeaderCell>
            <MasterEditCell dataKey="status" onChange={handleDataChanges} />
          </Column>

          {showCreate ? (
            <Column flexGrow={1} align="center">
              <CustomHeaderCell>{t("ACTION")}</CustomHeaderCell>
              <ActionCell
                dataKey="id"
                onClick={handleEditData}
                onDelete={(id: any) => {
                  setOpenDeleteModal(true);
                  setBrandId(id);
                }}
              />
            </Column>
          ) : (
            ""
          )}
            </Table>
          </div>
        </Content>
        <Footer className="footer">
          <Pagination
            total={total}
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["total", "-", "limit", "|", "pager", "skip"]}
            limitOptions={appConstObj.limit_options}
            limit={rows}
            activePage={page}
            onChangePage={onPageChange}
            onChangeLimit={handleChangeRows}
          />
        </Footer>
      </Container>
      <AlertModal
        open={openNoBrandModal}
        handleClose={(e: any) => {
          if (e) {
            setOpenNoBrandModal(false);
          }
        }}
        displayText={t("PLEASE_SELECT") + " " + t("BRAND")}
        modalTitle={t("PLEASE_SELECT")}
        cancelButton={false}
      />
      <AlertModal
        open={openDeleteModal}
        handleClose={(e: any) => {
          if (e == true) {
            handleDataDelete(brandId);
            setOpenDeleteModal(false);
          } else {
            setOpenDeleteModal(false);
          }
        }}
        displayText={t("CONFIRM_ACTION", {
          action: t("DELETE").toLowerCase(),
          entity: t("BRAND").toLowerCase(),
        })}
        modalTitle={t("CONFIRM_ACTION_TITLE", {
          action: t("BRAND").toLowerCase(),
        })}
      />
    </div>
  );
}
export default Brands;
